import { useTranslation } from 'react-i18next'
import { ChevronDown, Settings2 } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { cn } from '@/lib/utils'

export interface AdvancedSectionProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /**
   * How many of the settings inside differ from their defaults, shown on the
   * button so nothing changed stays hidden unnoticed while it is closed.
   */
  activeCount?: number
  /** Extra buttons shown in a row beside the toggle, e.g. to add an optional field. */
  actions?: React.ReactNode
  children: React.ReactNode
  className?: string
}

/**
 * Settings most people never touch, tucked behind an "Advanced settings"
 * button that opens them in place.
 */
export function AdvancedSection({ open, onOpenChange, activeCount = 0, actions, children, className }: AdvancedSectionProps) {
  const { t } = useTranslation()
  return (
    <Collapsible open={open} onOpenChange={onOpenChange} className={cn('flex flex-col gap-4', className)}>
      <div className="flex flex-wrap items-center gap-2">
        <CollapsibleTrigger asChild>
          <Button type="button" variant="outline" size="sm">
            <Settings2 className="size-4" />
            {open ? t('common.hideAdvanced') : t('common.showAdvanced')}
            {!open && activeCount > 0 && <Badge variant="secondary">{activeCount}</Badge>}
            <ChevronDown className={cn('size-4 transition-transform', open && 'rotate-180')} />
          </Button>
        </CollapsibleTrigger>
        {actions}
      </div>
      <CollapsibleContent className="flex flex-col gap-4">{children}</CollapsibleContent>
    </Collapsible>
  )
}
